import { useAppRouter } from '@/common/route/useAppRouter';
import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { StringUtils } from '@/common/utils/stringUtils';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useAvatarColorList } from '@/modules/avatar/color/model/list';
import { useAvatarLayerList } from '@/modules/avatar/layer/model/list';
import { ROUTE_NAME_AVATAR } from '@/modules/avatar/router';
import { avatarService } from '@/services/avatar';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useAvatarImageList() {
    const route = useRoute();
    const router = useAppRouter();
    const { executeAction, confirmAndExecute } = useCrudActions();

    const model = reactive({
        itemId: StringUtils.toNumber(route.params.itemId),
        itemName: null,
        categoryId: null
    });

    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const { colorMap, fetchList: fetchColors } = useAvatarColorList();
    const { layerMap, fetchList: fetchLayers } = useAvatarLayerList();

    const loadAndSetAvatarItem = executeAction({
        canExecute: () => model.itemId != null,
        request: () => avatarService.item.get(model.itemId),
        onSuccess: ({ data }) => {
            model.itemName = data.name;
            model.categoryId = data.categoryId;
        },
        onError: () => router.push({ name: ROUTE_NAME_AVATAR.ITEM.INDEX })
    });

    const { list, total, fetchList } = useBaseListView({
        request: avatarService.image.list,
        paramsBuilder: () => ({ ...query, itemId: model.itemId }),
        hooks: {
            loadInitialData: async () => await Promise.all([fetchColors(), fetchLayers(), loadAndSetAvatarItem()]),
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute,
            transformResponse: (list: any[]) =>
                list.map(item => {
                    const colorInfo = colorMap.value.get(item.colorId);
                    return {
                        ...item,
                        color: `<font color="${colorInfo?.color}"><i class="fas fa-palette"></i> ${colorInfo?.name}</font>`,
                        layer: layerMap.value.get(item.layerId).name
                    };
                })
        }
    });

    const actions = {
        remove: confirmAndExecute({
            confirmMessage: 'Are you sure you want to delete this image?',
            request: avatarService.image.remove,
            onSuccess: () => fetchList()
        })
    };

    return { model, list, total, query, fetchList, actions };
}
